import clsx from 'clsx';
import { isEmpty, toNumber } from 'lodash';
import { bool } from 'prop-types';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { Badge, Card, DashboardContext, Icon, ReactTable, Skeleton, TableCard, TabsContext } from 'components';
import { formatDate, formatLocalizedCurrency, getBadgeColor, getValidDate } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

const orderFilters = ['all', 'travels', 'leisures'];

const OrdersTable = ({ dashboardOrder = false, limit = false }) => {
  const { tabs } = useContext(DashboardContext);
  const { t } = useTranslation();
  const {
    call: ordersCall,
    loading: ordersLoading,
    data: ordersData,
    setData: setOrders
  } = useFetch({ withSetState: false });
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [filteredOrders, setFilteredOrders] = useSafeState(null);
  const [currentFilter, setCurrentFilter] = useSafeState(orderFilters[0]);
  const { setCurrentTab } = useContext(TabsContext);

  useEffect(() => {
    if (currentUser?.serialNumber) {
      getOrders();
    }
  }, [currentUser]);

  const getOrders = async () => {
    const { data } =
      (await ordersCall({
        service: 'castelis',
        path: `order/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    if (!isEmpty(data?.body?.orders)) {
      const orders = limit ? data.body.orders.slice(0, 5) : data.body.orders;
      setOrders(orders);
      setFilteredOrders(formattedOrders(orders));
    }
  };

  const formattedOrders = (orders) => {
    return orders.map((order) => {
      // Convert order date (dd/mm/yyyy) to Date object to enable sorting inside table
      return { ...order, date: getValidDate(order.date) };
    });
  };

  const orderColumns = useMemo(() => {
    const columns = [
      {
        accessorKey: 'date',
        header: t('dashboard:table.header.date'),
        cell: (cell) => <span>{formatDate(cell.getValue())}</span>
      },
      {
        accessorKey: 'ref',
        header: t('dashboard:table.header.order'),
        enableSorting: false,
        cell: (cell) => (
          <>
            <span className="font-light text-gray-500">{cell.row.original.ref}</span>
            <span className="font-bold truncate-line one-line">{cell.row.original.title}</span>
          </>
        )
      },
      {
        accessorKey: 'amount',
        header: t('dashboard:table.header.amount'),
        cell: (cell) => <span className="text-right">{formatLocalizedCurrency(toNumber(cell.getValue()))}</span>
      },
      {
        accessorKey: 'docs',
        header: t('dashboard:table.header.document'),
        cell: (cell) => (
          <>
            {!isEmpty(cell.getValue()) && <span className="text-gray-500 font-light">{t('dashboard:available')}</span>}
          </>
        )
      },
      {
        accessorKey: 'status',
        header: t('dashboard:table.header.status'),
        cell: (cell) => {
          return <Badge color={getBadgeColor(cell.getValue())}>{cell.getValue()}</Badge>;
        }
      },
      {
        accessorKey: 'action',
        header: '',
        enableSorting: false,
        cell: () => (
          <div className="order-arrow">
            <Icon name="long-arrow-right" />
          </div>
        )
      }
    ];

    if (dashboardOrder) {
      columns.splice(3, 0, {
        accessorKey: 'balance',
        header: t('dashboard:table.header.balance'),
        cell: (cell) => <span className="text-right">{formatLocalizedCurrency(toNumber(cell.getValue()))}</span>
      });

      columns.splice(5, 0, {
        accessorKey: 'delivery',
        header: t('dashboard:table.header.delivery'),
        cell: (cell) => <span className="text-right">{cell.row.original.delivery}</span>
      });
    }

    return columns;
  }, [filteredOrders, dashboardOrder]);

  // Utilisez maintenant updatedColumns pour vos opérations

  const onFilterOrders = (filter) => {
    if (filter === currentFilter) return;

    const orders = ordersData.filter((order) => {
      if (filter === 'travels' && order.type === 'Voyage') return order;
      if (filter === 'leisures' && order.type === 'Loisir') return order;
      if (filter === 'all') return order;
      return false;
    });

    setCurrentFilter(filter);
    setFilteredOrders(formattedOrders(orders));
  };

  const goToOrder = (row) => {
    const orderTab = tabs.find((t) => t.identifier === 'orders');
    const orderPath =
      row.type === 'Loisir'
        ? generatePath(routes.dashboard.children.leisureOrderShow.path, { id: row.id })
        : generatePath(routes.dashboard.children.travelOrderShow.path, { id: row.id });

    setCurrentTab(orderTab.identifier);

    navigate(orderPath);
  };

  return (
    <>
      {ordersLoading && (
        <div className="order-skeleton">
          <Skeleton type="text" classNames="h-full w-full" />
        </div>
      )}
      {!ordersLoading && filteredOrders && (
        <Card onMobile={false}>
          <h2 className="h2 underlined-yellow mb-4 hidden lg:block">
            {t(`dashboard:table.${appService.brand().toLowerCase()}Title`)}
          </h2>

          <div className="flex gap-2 mb-4 lg:border-b lg:border-gray-200 lg:mb-8">
            {orderFilters.map((filter, index) => (
              <button
                type="button"
                className={clsx('filter-btn', currentFilter === filter && 'active')}
                key={`order-filter-${index}`}
                onClick={() => onFilterOrders(filter)}
              >
                {t(`buttons.${filter}`)}
              </button>
            ))}
          </div>

          <div className="hidden lg:block">
            <ReactTable
              columns={orderColumns}
              data={filteredOrders}
              handleRowClick={goToOrder}
              pagination={!limit && filteredOrders?.length > 10}
            />
          </div>
          {dashboardOrder && (
            <div className="lg:hidden flex flex-col gap-4">
              {filteredOrders.map((order, index) => (
                <TableCard row={order} key={`order-${index}`} handleRowClick={goToOrder} />
              ))}
            </div>
          )}
        </Card>
      )}
    </>
  );
};

OrdersTable.propTypes = {
  dashboardOrder: bool,
  limit: bool
};

export default OrdersTable;
